import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import moment from "moment-timezone";
import ApiFactory from "services/api";
import Zebra from "zebra";

const initialState = {
  siteId: undefined,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  isFetching: false,
  currentVisits: [],
  currentVisit: undefined,
  currentResident: undefined,
  currentVisitor: undefined,
  appName: undefined,
  companyName: undefined,
  allowBadgeLost: false,
  maxVisitDuration: 1,
  badgeLostCompany: undefined,
  visitorList: [],
  residentList: [],
  timezone: "Europe/Paris",
  ephemeralCode: undefined,
  printer: {
    isReady: false,
    isError: false,
    errorIgnored: false,
    ipAddress: "unknown",
    requiredIp: "unknwon",
    connexion: "unknown",
    name: "unknown",
    error: "",
    pause: false,
    offline: false,
    paperOut: false,
    disablePrinter: false,
  },
  useKeyCard: false,
  userIsAuthorized: true,
};

export const API = ApiFactory();
export const Printer = new Zebra();

const slice = createSlice({
  name: "visits",
  initialState,
  reducers: {
    updateAuthorization: (state, action) => {
      return {
        ...state,
        userIsAuthorized: action.payload,
      };
    },
    updateConfig: (state, action) => {
      return {
        ...state,
        appName: action.payload.appName,
        companyName: action.payload.companyName,
        allowBadgeLost: action.payload.allowBadgeLost,
        badgeLostCompany: action.payload.badgeLostCompany,
        disablePrinter: !!action.payload.disablePrinter,
        useKeyCard: !!action.payload.useKeyCard,
        maxVisitDuration: action.payload.maxVisitDuration,
      };
    },
    updateVisits: (state, action) => {
      const user = action.payload.user;
      return {
        ...state,
        currentResident: user ? (user.isVisitor ? undefined : user) : undefined,
        currentVisitor: user ? (user.isVisitor ? user : undefined) : undefined,
        page: action.payload.currentPage,
        totalPages: action.payload.totalPages,
        totalItems: action.payload.totalItems,
        currentVisits: action.payload.visitList,
      };
    },
    residentListUpdated: (state, action) => {
      return {
        ...state,
        residentList: action.payload,
      };
    },
    visitorListUpdated: (state, action) => {
      return {
        ...state,
        visitorList: action.payload,
      };
    },
    resetVisitorList: (state, action) => {
      return {
        ...state,
        visitorList: [],
      };
    },
    updateCurrentVisit: (state, action) => {
      return {
        ...state,
        currentVisit: action.payload,
      };
    },
    updateCurrentResident: (state, action) => {
      return {
        ...state,
        currentResident: action.payload,
      };
    },
    printerErrorIgnored: (state, action) => {
      return {
        ...state,
        printer: {
          ...state.printer,
          errorIgnored: true,
        },
      };
    },
    notifyPrinterError: (state, action) => {
      return {
        ...state,
        printer: {
          ipAddress: "",
          connexion: "",
          name: "",
          errorIgnored: false,
          isReady: false,
          isError: true,
          error: action.payload,
        },
      };
    },
    notifyPrinterFunctional: (state, action) => {
      return {
        ...state,
        printer: {
          isReady: true,
          isError: false,
          errorIgnored: false,
          ipAddress: action.payload.ipAddress,
          connexion: action.payload.connexion,
          name: action.payload.name,
          error: "",
          pause: false,
          offline: false,
          paperOut: false,
          requiredIp: action.payload.requiredIp,
        },
      };
    },
    notifyPrinterStatus: (state, action) => {
      return {
        ...state,
        printer: {
          ...state.printer,
          pause: action.payload.pause,
          offline: action.payload.offline,
          paperOut: action.payload.paperOut,
        },
      };
    },
    useFoundPrinter: (state, action) => {
      return {
        ...state,
        printer: {
          ...state.printer,
          requiredIp: state.printer.ipAddress,
        },
      };
    },
    resetPrinterStatus: (state, action) => {
      return {
        ...state,
        printer: {
          isReady: false,
          isError: false,
          errorIgnored: false,
          ipAddress: "unknown",
          requiredIp: "unknwon",
          connexion: "unknown",
          name: "unknown",
          error: "",
          pause: false,
          offline: false,
          paperOut: false,
        },
      };
    },
    startFeching: (state, action) => {
      return {
        ...state,
        isFetching: true,
      };
    },
    stopFeching: (state, action) => {
      return {
        ...state,
        isFetching: false,
      };
    },
    ephemaralCodeUpdated: (state, action) => {
      return {
        ...state,
        ephemeralCode: action.payload,
      };
    },
  },
});

export const {
  updateConfig,
  updateVisits,
  residentListUpdated,
  visitorListUpdated,
  resetVisitorList,
  updateCurrentVisit,
  printerErrorIgnored,
  notifyPrinterError,
  notifyPrinterFunctional,
  notifyPrinterStatus,
  useFoundPrinter,
  resetPrinterStatus,
  startFeching,
  stopFeching,
  updateCurrentResident,
  ephemaralCodeUpdated,
  updateAuthorization,
} = slice.actions;

export const initialize = keycloak => async (dispatch, getState) => {
  const appId = process.env.REACT_APP_ID;
  const siteId = process.env.REACT_APP_SITE_ID;
  const viewId = process.env.REACT_APP_VIEW_ID;

  API.initialize(appId, siteId, viewId, keycloak);
  dispatch(initializePrinter());
};

export const initializePrinter = () => async (dispatch, getState) => {
  try {
    const config = await API.getConfig();
    dispatch(startFeching());
    dispatch(resetPrinterStatus());
    dispatch(updateConfig(config));

    if (!config.disablePrinter) {
      await Printer.initialize(config.printerIp);
      dispatch(
        notifyPrinterFunctional({
          ipAddress: Printer.printerIp,
          connexion: Printer.connexion,
          name: Printer.printerName,
          requiredIp: config.printerIp,
        }),
      );
    }
    dispatch(updateAuthorization(true));
  } catch (e) {
    dispatch(updateAuthorization(false));
  } finally {
    dispatch(stopFeching());
  }
};

export const updatePrinterStatus = () => async (dispatch, getState) => {
  if (getState().visits.printer.isReady) {
    const status = await Printer.getStatus();
    const { offline, paperOut, paused } = status;
    dispatch(notifyPrinterStatus({ offline, paperOut, paused }));
  }
};

export const searchResident = text => async (dispatch, getState) => {
  if (!text || text.length < 3) return dispatch(residentListUpdated([]));

  let result = await API.users.searchResident(text);
  dispatch(residentListUpdated(result.map(res => Object.assign(res, { isVisitor: false }))));
};

export const searchVisitor = text => async (dispatch, getState) => {
  if (!text || text.length < 3) return dispatch(visitorListUpdated([]));

  let result = await API.users.searchVisitor(text);
  dispatch(visitorListUpdated(result.map(res => Object.assign(res, { isVisitor: true }))));
};

export const updateVisitsFromUser = user => async (dispatch, getState) => {
  try {
    dispatch(startFeching());

    const { timezone, page, pageSize } = getState().visits;

    const criteria = {
      residentId: user.isVisitor ? undefined : user.id,
      visitorId: user.isVisitor ? user.id : undefined,
      from: moment().tz(timezone).startOf("day").toISOString(),
      to: moment().tz(timezone).endOf("day").toISOString(),
    };

    const visits = await API.visits.search(criteria, page, pageSize);
    dispatch(
      updateVisits({
        user,
        page,
        totalPages: visits.totalPages,
        totalItems: visits.totalItems,
        visitList: visits.items.filter(
          vis => vis.visitors.findIndex(v => v.id === vis.resident.id) === -1,
        ),
      }),
    );
    return visits.items;
  } finally {
    dispatch(stopFeching());
  }
};

export const updateVisitsFromDay = momentDate => async (dispatch, getState) => {
  try {
    dispatch(startFeching());

    const { timezone } = getState().visits;

    const criteria = {
      residentId: undefined,
      visitorId: undefined,
      from: momentDate.tz(timezone).startOf("day").toISOString(),
      to: momentDate.tz(timezone).endOf("day").toISOString(),
      externalVisitorsOnly: true,
    };

    const visits = await API.visits.search(criteria, 1, 100000);
    dispatch(
      updateVisits({
        user: undefined,
        page: 1,
        totalPages: visits.totalPages,
        totalItems: visits.totalItems,
        visitList: visits.items,
      }),
    );
    return visits.items;
  } finally {
    dispatch(stopFeching());
  }
};

export const createVisit =
  (startDate, endDate, visitors, resident, duration = 1) =>
  async (dispatch, getState) => {
    try {
      const visit = await API.visits.create(startDate, endDate, visitors, resident, duration);
      dispatch(updateCurrentVisit(visit));
      return visit;
    } catch (error) {
      console.error(error);
    }
  };

export const createVisitor =
  ({ login, email, firstname, lastname, company, phone }) =>
  async (dispatch, getState) => {
    try {
      const visitor = await API.users.createVisitor({
        login,
        email,
        firstname,
        lastname,
        company,
        phone,
      });
      return visitor;
    } catch (error) {
      console.error(error);
    }
  };

export const updateVisit =
  (visitId, startDate, endDate, visitors, resident) => async (dispatch, getState) => {
    try {
      const visit = await API.visits.update(visitId, startDate, endDate, visitors, resident);
      dispatch(updateCurrentVisit(visit));
      return visit;
    } finally {
      dispatch(startFeching(false));
    }
  };

export const cancelVisit = visitId => async (dispatch, getState) => {
  try {
    await API.visits.cancel(visitId);
    dispatch(updateCurrentVisit());
  } finally {
    dispatch(startFeching(false));
  }
};

export const selecteDefaultPrinter = () => async (dispatch, getState) => {
  dispatch(useFoundPrinter());
};

export const printBadge = visitor => async (dispatch, getState) => {
  let toPrint;
  let badgeContent;
  const { disablePrinter } = getState().visits;

  try {
    const badge = await API.badges.create(visitor.visitId, visitor.id);
    if (badge.error) {
      return badge;
    }
    const { zplText } = disablePrinter
      ? { zplText: "" }
      : await API.badges.getZPL(badge.badgeContent);
    badgeContent = badge.badgeContent;
    toPrint = zplText;
  } catch (error) {
    console.error(error);
    throw Error("Server create badge error");
  }

  if (!disablePrinter) await Printer.print(toPrint);

  return badgeContent;
};

export const isVisitValid = (visitId, visitorId) => async (dispatch, getState) => {
  try {
    return await API.visits.isValid(visitId, visitorId);
  } catch (error) {
    console.error(error);
    throw Error("check visit error");
  }
};
export const markBadgeCheckin = badgeContent => async (dispatch, getState) => {
  try {
    await API.badges.checkIn(badgeContent);
  } catch (error) {
    console.error(error);
    throw Error("Mark badge as checkin error");
  }
};
export const associateKeyCard = (badgeContent, badgeRef) => async (dispatch, getState) => {
  try {
    await API.badges.keyCard.associate(badgeContent, badgeRef);
  } catch (error) {
    console.error(error);
    throw Error("Associate badge error");
  }
};
export const releaseKeyCards = (badgeRefs, markAsExited) => async (dispatch, getState) => {
  try {
    await API.badges.keyCard.release(badgeRefs, markAsExited);
  } catch (error) {
    console.error(error);
    throw Error("Release badge error");
  }
};
export const saveKeyCardsNote = (badgeRef, notes) => async (dispatch, getState) => {
  try {
    await API.badges.keyCard.updateNotes(badgeRef, notes);
  } catch (error) {
    console.error(error);
    throw Error("Release badge error");
  }
};
export const listKeyCards =
  (search, page, size, onlyWarning, hideLost) => async (dispatch, getState) => {
    try {
      return await API.badges.keyCard.list(search, page, size, onlyWarning, hideLost);
    } catch (error) {
      console.error(error);
      throw Error("Release badge error");
    }
  };
export const getKeyCardOwner = badgeRef => async (dispatch, getState) => {
  try {
    return await API.badges.keyCard.getOwner(badgeRef);
  } catch (error) {
    console.error(error);
    throw Error("Get badge owner error");
  }
};

export const markBadgeCheckout = badgeContent => async (dispatch, getState) => {
  try {
    await API.badges.checkOut(badgeContent);
  } catch (error) {
    console.error(error);
    throw Error("mark badge as checkout error");
  }
};

export const createVisitForBadgeLost = resident => async (dispatch, getState) => {
  try {
    const startDate = moment();
    const endDate = moment().endOf("day");
    const visit = await API.visits.create(startDate, endDate, [resident], resident);
    dispatch(updateCurrentVisit(visit));
    return visit;
  } catch (error) {
    console.error(error);
  }
};

export const downloadStatus = () => async (dispatch, getState) => {
  try {
    let result = await API.downloadStatus();
    const href = URL.createObjectURL(result);
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", `Export_visits_${moment().format("YYYYMMDD")}.xlsx`);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  } catch (error) {
    console.error(error);
    throw Error("download status error");
  }
};

export const extractVisits = (startDate, endDate) => async (dispatch, getState) => {
  try {
    let result = await API.extractVisits(startDate, endDate);
    const href = URL.createObjectURL(result);
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", `Extract_visits_${startDate}_${endDate}.xlsx`);
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  } catch (error) {
    console.error(error);
    throw Error("extract visits error");
  }
};

export const analyseCode = code => async (dispatch, getState) => {
  try {
    dispatch(startFeching());
    // workaround qrcode from legacy app
    code = code.replace("/", ":");
    const result = await API.analyseCode(code);
    return result;
  } catch (error) {
    return;
  } finally {
    dispatch(stopFeching());
  }
};

export const getEphemeralCode = userId => async (dispatch, getState) => {
  try {
    const result = await API.users.getEphemeralCode({ userId });
    dispatch(ephemaralCodeUpdated(result.code));
    return result;
  } catch (error) {
    return;
  } finally {
  }
};

export const listVisitorsHistory = search => async (dispatch, getState) => {
  try {
    const result = await API.users.listVisitorsHistory(search);

    return result;
  } catch (error) {
    return;
  } finally {
  }
};

export const lostKeyCards = keycards => async (dispatch, getState) => {
  try {
    await Promise.all(keycards.map(keycard => API.badges.keyCard.lost(keycard)));
  } catch (error) {
    return;
  } finally {
  }
};

export default slice.reducer;
